import { useEventListener, useLatest } from 'ahooks';
import classNames from 'classnames';
import { cloneDeep, isEqual, range, sum } from 'lodash';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Message } from '@meitu/saas-ui';
import { useFramework } from '@/framework';
import { useLocaleStore } from '@/i18n/index';
import { arePropsEqual, registerStyleSheet } from '@/utils/common-kit';
import { useResizeCursor } from '@/utils/cursor-kit';
import type { IPoint, ITableLayer, ITableLayerProps, RowOrColSelectedRectInfo } from '@/version-types';
import {
    LINE_BOLD,
    LINE_HOVER_BOLD,
    MIN_BASE_CELL_HEIGHT,
    MIN_BASE_CELL_WIDTH,
    SIDE_BAR_BOLD,
} from '../../../default-configs';
import addOrUpdateLayer from '../../../utils/add-or-update-layer';
import { getFixedTableNumber } from '../../../utils/fix-number';
import {
    alignTableToVisualOrigin,
    getTableLocalPointByScenePoint,
    getTableOuterInsets,
    getTableVisualOrigin,
} from '../../../utils/geometry';
import { hoverAreaClassName } from '../../../utils/table';
import { applyTableLayout, getTableColumnMinimumWidths, getTableRowMinimumSize } from '../../../utils/table-layout';
import { useTableEditState } from '../state';
import { getTableFramePoint } from '../utils/get-table-frame-point';
import './table-controller.less';

type TableInfo = Pick<ITableLayer, 'id' | 'width' | 'height' | 'top' | 'left' | 'rotate'>;

interface TableControllerProps {
    tableInfo: TableInfo;
    tableLayerData: ITableLayerProps;
}

const hiddenLine = () => {
    const removeStyle = registerStyleSheet(`
        .tableController-lines {
            display: none;
        }
    `);
    return removeStyle;
};

const TableController = memo<TableControllerProps>(
    ({ tableInfo, tableLayerData }) => {
        const {
            baseCells: { horizontal: hSizes, vertical: vSizes },
        } = tableLayerData;

        const { setState } = useTableEditState();
        const rowOrColSelectedRectInfo = useTableEditState()((s) => s.rowOrColSelectedRectInfo);

        const tableFakeBorder = getTableOuterInsets(tableLayerData);

        const { selectedRectNode, onSelectedRowOrCol } = useRowOrColSelectedRect({
            tableInfo,
            tableLayerData,
            tableFakeBorder,
        });
        const lines = useMemo(
            () => getLines(tableLayerData, tableFakeBorder),
            [tableLayerData, JSON.stringify(tableFakeBorder)],
        );
        const { movingLineId, onLineMouseDown: _onLineMouseDown } = useDragLine(tableInfo.id, lines);
        const _onLineMouseDownRef = useLatest(_onLineMouseDown);

        const cursorClassNames = useResizeCursor(tableInfo.rotate);

        useEffect(() => {
            if (!movingLineId) return;
            const cursorClassName = movingLineId.direction === 'vertical' ? cursorClassNames.LC : cursorClassNames.TC;
            document.body.classList.add(cursorClassName);
            return () => {
                document.body.classList.remove(cursorClassName);
            };
        }, [!!movingLineId]);

        const onLineMouseDown = useCallback(
            (e: React.MouseEvent<HTMLDivElement, MouseEvent>, line: Line) => {
                // 阻止tableLayer的移动操作
                e.stopPropagation();
                const started = _onLineMouseDownRef.current?.(e, line.lineId);
                if (!started) return;

                setState({
                    cellsIsSelecting: false,
                    lineIsDragging: true,
                });
            },
            [_onLineMouseDownRef, setState],
        );

        const sideBarBoldCss = { '--sideBarBold': `${SIDE_BAR_BOLD}px` } as any;
        const linesCss = { '--lineHoverBold': `${LINE_HOVER_BOLD}px`, '--lineBold': `${LINE_BOLD}px` } as any;
        const res = (
            <div className="tableController">
                {selectedRectNode}
                <div className="tableController-sideBar" style={sideBarBoldCss}>
                    <div className="tableController-cornerBtn" onMouseDown={(e) => e.stopPropagation()} />
                    {(['horizontalSide', 'verticalSide'] as const).map((sideMode) => {
                        const sizes = sideMode === 'horizontalSide' ? hSizes : vSizes;
                        return (
                            <div key={sideMode} className={`tableController-${sideMode}`}>
                                <div className="tableController-sideBtns">
                                    {sizes.map((size, index) => {
                                        const isLast = sizes.length - 1 === index;
                                        const cellSize = (() => {
                                            let cellSize = size;
                                            if (index == 0)
                                                cellSize +=
                                                    sideMode === 'horizontalSide'
                                                        ? tableFakeBorder.left
                                                        : tableFakeBorder.top;
                                            if (isLast)
                                                cellSize +=
                                                    sideMode === 'horizontalSide'
                                                        ? tableFakeBorder.right
                                                        : tableFakeBorder.bottom;
                                            return cellSize;
                                        })();
                                        const leftOrTop =
                                            index === 0
                                                ? 0
                                                : (sideMode === 'horizontalSide'
                                                      ? tableFakeBorder.left
                                                      : tableFakeBorder.top) + sum(sizes.slice(0, index));
                                        const isSelected =
                                            rowOrColSelectedRectInfo?.direction ===
                                                (sideMode === 'horizontalSide' ? 'vertical' : 'horizontal') &&
                                            rowOrColSelectedRectInfo?.index === index;
                                        return (
                                            <div
                                                key={`${sideMode}-${index}`}
                                                className="tableController-sideBtn"
                                                data-is-selected={isSelected}
                                                style={
                                                    sideMode === 'horizontalSide'
                                                        ? { width: cellSize, left: leftOrTop }
                                                        : { height: cellSize, top: leftOrTop }
                                                }
                                            ></div>
                                        );
                                    })}
                                </div>
                                <div className="sideHoverAreas">
                                    {range(sizes.length + 1).map((index) => (
                                        <SideBarHoverArea
                                            key={`${sideMode}-${index}`}
                                            tableLayerData={tableLayerData}
                                            tableFakeBorder={tableFakeBorder}
                                            direction={sideMode === 'horizontalSide' ? 'horizontal' : 'vertical'}
                                            rowOrColIndex={index}
                                            onSelectedRowOrCol={(index) =>
                                                onSelectedRowOrCol({
                                                    direction:
                                                        sideMode === 'horizontalSide' ? 'vertical' : 'horizontal',
                                                    index,
                                                })
                                            }
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="tableController-lines" style={linesCss}>
                    {movingLineId ? (
                        <>
                            <LineComponent
                                key={`${movingLineId.direction}-${movingLineId.index}`}
                                line={
                                    lines.find(
                                        (l) =>
                                            l.lineId.direction === movingLineId.direction &&
                                            l.lineId.index === movingLineId.index,
                                    )!
                                }
                                tableRotate={tableInfo.rotate}
                                onMouseDown={onLineMouseDown}
                            />
                            <style
                                dangerouslySetInnerHTML={{
                                    __html: `
                                        .tableController-line[data-line-id="${movingLineId.direction}-${movingLineId.index}"]
                                        > .tableController-miniLine::before {
                                            background-color: var(--content-system-link, #38f);
                                        }
                                        .tableController-line:hover
                                        > .tableController-miniLine::before{
                                            background-color: initial !important;
                                        }
                                        .tableController-miniLine {
                                            cursor: inherit !important;
                                        }
                                    `,
                                }}
                            />
                        </>
                    ) : (
                        lines.map((line) => (
                            <LineComponent
                                key={`${line.lineId.direction}-${line.lineId.index}`}
                                line={line}
                                tableRotate={tableInfo.rotate}
                                onMouseDown={onLineMouseDown}
                            />
                        ))
                    )}
                </div>
            </div>
        );

        return res;
    },
    (preProps, nextProps) => {
        return isEqual(preProps.tableInfo, nextProps.tableInfo) && arePropsEqual(preProps, nextProps, ['tableInfo']);
    },
);

TableController.displayName = 'TableController';

export default TableController;

const useDragLine = (layerId: string, lines: Line[]) => {
    const framework = useFramework();
    const { handleDragLineStart, handleDragLine, handleDragLineEnd } = useHandleDragLine(layerId);
    const { setState } = useTableEditState();

    const [movingLineId, setMovingLineId] = useState<LineId | null>(null);
    const movingLineIdRef = useLatest(movingLineId);
    const movingInfoRef = useRef<{
        hasMouseDown: boolean;
        startMovePoint: {
            x: number;
            y: number;
        } | null;
        dragStartTableLayer: ITableLayer | null;
    }>({
        hasMouseDown: false,
        startMovePoint: null,
        dragStartTableLayer: null,
    });

    const getLocalMovePoint = useCallback(
        ({ clientX, clientY }: Record<'clientX' | 'clientY', number>, dragStartTableLayer?: ITableLayer | null) => {
            const tableLayer =
                dragStartTableLayer ?? (framework.service.flatLayer.getLayer(layerId) as ITableLayer | undefined);
            if (!tableLayer) return null;

            const scenePoint = getTableFramePoint(framework.service.transform, { x: clientX, y: clientY }, tableLayer);
            return getTableLocalPointByScenePoint({ point: scenePoint, tableLayer });
        },
        [framework.service.flatLayer, framework.service.transform, layerId],
    );

    // 解决最侧边拖拽线与图层拉伸按钮同时出现的问题
    {
        const removeStyleRef = useRef<(() => void) | null>(null);

        useEffect(() => {
            return () => {
                removeStyleRef.current?.();
                removeStyleRef.current = null;
            };
        }, []);

        useEventListener('mousemove', (e) => {
            if (movingLineIdRef.current) return;
            if (checkIsInResizeArea(e)) {
                removeStyleRef.current?.();
                const removeStyle = hiddenLine();
                removeStyleRef.current = removeStyle;
            } else {
                removeStyleRef.current?.();
                removeStyleRef.current = null;
            }
        });
    }

    const checkIsInResizeArea = (e: Record<'clientX' | 'clientY', number>) => {
        const tableLayer = framework.service.flatLayer.getLayer(layerId) as ITableLayer | undefined;
        if (!tableLayer) return false;

        const localPoint = getLocalMovePoint(e, movingInfoRef.current.dragStartTableLayer ?? tableLayer);
        if (!localPoint) return false;

        const handleHitSize = 32 / framework.service.edgelessCanvas.zoom;
        const halfHandleHitSize = handleHitSize / 2;
        const hitRightResize =
            localPoint.x >= tableLayer.width - halfHandleHitSize &&
            localPoint.x <= tableLayer.width + halfHandleHitSize &&
            localPoint.y >= tableLayer.height / 2 - halfHandleHitSize &&
            localPoint.y <= tableLayer.height / 2 + halfHandleHitSize;
        const hitBottomResize =
            localPoint.y >= tableLayer.height - halfHandleHitSize &&
            localPoint.y <= tableLayer.height + halfHandleHitSize &&
            localPoint.x >= tableLayer.width / 2 - halfHandleHitSize &&
            localPoint.x <= tableLayer.width / 2 + halfHandleHitSize;

        return hitRightResize || hitBottomResize;
    };

    const onLineMouseDown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, movingLineId: LineId) => {
        if (checkIsInResizeArea(e)) return false;
        const targetLine = lines.find((line) => isEqual(movingLineId, line.lineId));
        if (!targetLine) return false;
        const tableLayer = framework.service.flatLayer.getLayer(layerId) as ITableLayer | undefined;
        if (!tableLayer) return false;
        const dragStartTableLayer = cloneDeep(tableLayer);
        const localPoint = getLocalMovePoint(e, dragStartTableLayer);
        if (!localPoint) return false;

        const dragLineStarted = handleDragLineStart(movingLineId);
        if (!dragLineStarted) return false;

        movingInfoRef.current = {
            hasMouseDown: true,
            startMovePoint: localPoint,
            dragStartTableLayer,
        };
        setMovingLineId(movingLineId);
        return true;
    };

    useEventListener('mousemove', ({ clientX, clientY }) => {
        const { hasMouseDown, startMovePoint, dragStartTableLayer } = movingInfoRef.current;
        if (!hasMouseDown) return;
        if (!startMovePoint) return;
        const localPoint = getLocalMovePoint({ clientX, clientY }, dragStartTableLayer);
        if (!localPoint) return;
        const deltaX = localPoint.x - startMovePoint.x;
        const deltaY = localPoint.y - startMovePoint.y;
        const activeMovingLineId = movingLineIdRef.current;
        if (!activeMovingLineId) return;
        const movingLine = lines.find((line) => isEqual(line.lineId, activeMovingLineId));
        if (!movingLine) return;
        handleDragLine(movingLine, { deltaX, deltaY });
    });

    useEventListener('mouseup', () => {
        movingInfoRef.current = {
            hasMouseDown: false,
            startMovePoint: null,
            dragStartTableLayer: null,
        };
        setMovingLineId(null);
        setState({
            lineIsDragging: false,
        });
        handleDragLineEnd();
    });

    return {
        movingLineId,
        onLineMouseDown,
    };
};

type LineId = { direction: 'horizontal' | 'vertical'; index: number };

type Line = {
    lineId: LineId;
    miniLines: MiniLine[];
};

type MiniLine = {
    /** miniLines中, 水平方向上的索引 */
    hIndex: number;
    /** miniLines中, 竖直方向上的索引 */
    vIndex: number;
    /** 相对于tableLayer左上角 */
    top: number;
    /** 相对于tableLayer左上角 */
    left: number;
    /** 线的方向 */
    direction: 'horizontal' | 'vertical';
    /** 线的长度 */
    lineLength: number;
};

const realLineBold = `calc(${LINE_HOVER_BOLD}px / var(--stage-scale, 1))`;

const getMiniLineStyle = (miniLine: MiniLine): React.CSSProperties => {
    return {
        top: miniLine.direction === 'vertical' ? miniLine.top : 0,
        left: miniLine.direction === 'vertical' ? 0 : miniLine.left,
        width: miniLine.direction === 'vertical' ? realLineBold : miniLine.lineLength,
        height: miniLine.direction === 'vertical' ? miniLine.lineLength : realLineBold,
    };
};

const getLineStyle = (line: Line): React.CSSProperties => {
    const miniLine = line.miniLines[0];
    const lineLen = line.miniLines.reduce((acc, l) => acc + l.lineLength, 0);
    return {
        position: 'absolute',
        top: miniLine.direction === 'vertical' ? 0 : miniLine.top,
        left: miniLine.direction === 'vertical' ? miniLine.left : 0,
        width: miniLine.direction === 'vertical' ? realLineBold : lineLen,
        height: miniLine.direction === 'vertical' ? lineLen : realLineBold,
        transform: miniLine.direction === 'vertical' ? 'translateX(-50%)' : 'translateY(-50%)',
    };
};

const LineComponent = memo<{
    line: Line;
    tableRotate?: number;
    onMouseDown?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>, line: Line) => void;
}>(({ line, tableRotate = 0, onMouseDown }) => (
    <div
        className="tableController-line"
        data-line-id={`${line.lineId.direction}-${line.lineId.index}`}
        style={getLineStyle(line)}
    >
        {line.miniLines.map((miniLine) => (
            <MiniLineComponent
                key={`${miniLine.direction}-${miniLine.hIndex}-${miniLine.vIndex}`}
                miniLine={miniLine}
                tableRotate={tableRotate}
                onMouseDown={(e) => onMouseDown?.(e, line)}
            />
        ))}
    </div>
));

LineComponent.displayName = 'LineComponent';

const MiniLineComponent: React.FC<{
    miniLine: MiniLine;
    tableRotate?: number;
    onMouseDown?: React.MouseEventHandler<HTMLDivElement>;
}> = ({ miniLine, tableRotate = 0, onMouseDown }) => {
    const cursorClassNames = useResizeCursor(tableRotate);

    return (
        <div
            className={classNames(
                'tableController-miniLine',
                miniLine.direction === 'vertical' ? cursorClassNames.LC : cursorClassNames.TC,
            )}
            data-line-direction={miniLine.direction}
            style={getMiniLineStyle(miniLine)}
            onMouseDown={onMouseDown}
        ></div>
    );
};

const getLines = (
    tableLayerData: ITableLayerProps,
    tableFakeBorder: Record<'top' | 'left' | 'right' | 'bottom', number>,
): Line[] => {
    const {
        baseCells: { horizontal: horizontalSizes, vertical: verticalSizes },
        cells: cellsProp,
    } = tableLayerData;

    const tops = [
        ...verticalSizes.map((_, index) => verticalSizes.slice(0, index).reduce((a, b) => a + b, 0)),
        sum(verticalSizes),
    ];
    const lefts = [
        ...horizontalSizes.map((_, index) => horizontalSizes.slice(0, index).reduce((a, b) => a + b, 0)),
        sum(horizontalSizes),
    ];

    const checkIsBorderMiniLine = (miniLine: MiniLine): boolean => {
        let isBorderLine = false;
        if (miniLine.direction === 'vertical') {
            if (miniLine.hIndex === horizontalSizes.length) isBorderLine = true;
            else {
                isBorderLine = cellsProp
                    .map((rowCells) => rowCells[miniLine.hIndex])
                    .some(
                        (cell) =>
                            cell && miniLine.vIndex >= cell.vIndex && miniLine.vIndex + 1 <= cell.vIndex + cell.rowSpan,
                    );
            }
        } else {
            if (miniLine.vIndex === verticalSizes.length) isBorderLine = true;
            else {
                isBorderLine = cellsProp[miniLine.vIndex]?.some(
                    (cell) =>
                        cell && miniLine.hIndex >= cell.hIndex && miniLine.hIndex + 1 <= cell.hIndex + cell.colSpan,
                );
            }
        }
        return isBorderLine;
    };

    const lines: Line[] = [];
    range(1, verticalSizes.length + 1).forEach((index) => {
        lines.push({
            __id: `horizontal-${index}`,
            lineId: {
                direction: 'horizontal',
                index,
            },
            miniLines: [],
        } as any);
    });
    range(1, horizontalSizes.length + 1).forEach((index) => {
        lines.push({
            __id: `vertical-${index}`,
            lineId: {
                direction: 'vertical',
                index,
            },
            miniLines: [],
        } as any);
    });
    for (let hIndex = 0; hIndex <= horizontalSizes.length; hIndex++) {
        const isLastHIndex = hIndex === horizontalSizes.length;
        const left = tableFakeBorder.left + lefts[hIndex];
        for (let vIndex = 0; vIndex <= verticalSizes.length; vIndex++) {
            const isLastVIndex = vIndex === verticalSizes.length;
            const top = tableFakeBorder.top + tops[vIndex];
            if (vIndex !== 0 && !isLastHIndex) {
                const miniLine: MiniLine = {
                    hIndex,
                    vIndex,
                    top,
                    left,
                    direction: 'horizontal',
                    lineLength: horizontalSizes[hIndex],
                };
                if (checkIsBorderMiniLine(miniLine)) {
                    const line__id = `horizontal-${vIndex}`;
                    lines.find((l) => (l as any).__id === line__id)?.miniLines.push(miniLine);
                }
            }
            if (hIndex !== 0 && !isLastVIndex) {
                const miniLine: MiniLine = {
                    hIndex,
                    vIndex,
                    top,
                    left,
                    direction: 'vertical',
                    lineLength: verticalSizes[vIndex],
                };
                if (checkIsBorderMiniLine(miniLine)) {
                    const line__id = `vertical-${hIndex}`;
                    lines.find((l) => (l as any).__id === line__id)?.miniLines.push(miniLine);
                }
            }
        }
    }
    return lines.filter((l) => l.miniLines.length);
};

const useHandleDragLine = (layerId: string) => {
    /**
     * 拖拽这两种线(https://xiuxiu-pro.meitudata.com/posters/9edeccf9f20bb83d55e490c3ba50d787.png)的时候, 如果没处理好, 就会导致这种bug (https://xiuxiu-pro.meitudata.com/posters/cf2a1b0f3ede8a4fc971fface747b3c1.png)
     * 这是解决思路:
     * 1. 应受到影响的几个tr取消高度(自由坍塌)
     * 2. 不应受影响且在这几个tr里的td高度写死, 防止变形
     * 只需要再给`应受影响且在这几个tr里的td`高度设置最小值, 就能达到极限时的布局效果
     * 拖拽过程中, 只需要再给`应受影响且在这几个tr里的td`高度设置值, 就能达到拖拽时的布局效果
     *
     * 注: 表格很大时, 拖拽会卡顿, 所以需要优化, 优化思路:
     * 拖拽时不考虑以上问题, 只需要考虑影响上一行, 拖拽后去自动修复, 只要保证最终布局不错乱就行
     *
     * TODO: 拖拽这两种线(https://xiuxiu-pro.meitudata.com/posters/9edeccf9f20bb83d55e490c3ba50d787.png)还是有bug:
     *  1. content为'111'的单元格的rowMinSize计算得不对 (不知道咋搞啊...)
     *  2. 不能只靠rowMinSize约束极限布局, 还需要靠线到表格顶部的最小值来约束, 不然拖拽'222'的单元格到最小值时, 也会有问题
     */

    const { $t } = useLocaleStore();
    const framework = useFramework();
    const tableLayerOnDragStartRef = useRef<ITableLayer | null>(null);
    const dragStartVisualOriginRef = useRef<IPoint | null>(null);
    const resultTableLayerRef = useRef<ITableLayer | null>(null);
    const intervalTimerRef = useRef<any>();
    const colMinSizesRef = useRef<number[]>([]);
    const rowMinSizeRef = useRef(MIN_BASE_CELL_HEIGHT);

    type DragData = { deltaX: number; deltaY: number };

    const resetStates = () => {
        colMinSizesRef.current = [];
        rowMinSizeRef.current = MIN_BASE_CELL_HEIGHT;
        clearInterval(intervalTimerRef.current);
        resultTableLayerRef.current = null;
        tableLayerOnDragStartRef.current = null;
        dragStartVisualOriginRef.current = null;
    };

    const getAffectedRowMinHeight = (tableLayer: ITableLayer, lineId: LineId) => {
        if (lineId.direction !== 'horizontal') return MIN_BASE_CELL_HEIGHT;
        return getTableRowMinimumSize(tableLayer, lineId.index - 1);
    };

    const handleDragLineStart = (lineId: LineId) => {
        resetStates();

        const tableLayer = framework.service.flatLayer.getLayer(layerId) as ITableLayer;
        if (!tableLayer) return false;

        const dragStartTableLayer = cloneDeep(tableLayer);
        tableLayerOnDragStartRef.current = dragStartTableLayer;
        dragStartVisualOriginRef.current = getTableVisualOrigin(dragStartTableLayer);
        resultTableLayerRef.current = cloneDeep(dragStartTableLayer);
        colMinSizesRef.current = getTableColumnMinimumWidths(tableLayer);
        rowMinSizeRef.current = getAffectedRowMinHeight(tableLayer, lineId);

        // 数据越多, render时长越久, 所需延迟就越多
        const delay = tableLayer.data.cells.flat().length * 0.01;

        intervalTimerRef.current = setInterval(() => {
            if (resultTableLayerRef.current) {
                if (!resultTableLayerRef.current.__runtime) {
                    resultTableLayerRef.current.__runtime = {};
                }
                // 拖拽中禁止数据的自动修复, 否则拖拽完后会偏差很大
                resultTableLayerRef.current.__runtime.disableAutoDataFix = true;
                resultTableLayerRef.current = addOrUpdateLayer({
                    frameworkIdentity: framework.identity,
                    layer: resultTableLayerRef.current,
                    resetMaterialIds: false,
                });
            }
        }, delay);
        return true;
    };

    const handleDragLineEnd = () => {
        if (resultTableLayerRef.current && tableLayerOnDragStartRef.current) {
            if (!resultTableLayerRef.current.__runtime) {
                resultTableLayerRef.current.__runtime = {};
            }
            // 还原状态
            delete resultTableLayerRef.current.__runtime.disableAutoDataFix;
            resultTableLayerRef.current = getFixedTableNumber(resultTableLayerRef.current);
            if (dragStartVisualOriginRef.current) {
                alignTableToVisualOrigin(resultTableLayerRef.current, dragStartVisualOriginRef.current);
            }
            resultTableLayerRef.current = addOrUpdateLayer({
                frameworkIdentity: framework.identity,
                layer: resultTableLayerRef.current,
                resetMaterialIds: false,
            });
            framework.service.frame.setActiveLayer(resultTableLayerRef.current.id);
            framework.service.history.write(cloneDeep(tableLayerOnDragStartRef.current), resultTableLayerRef.current);
        }
        resetStates();
    };

    const handleDragLine = (line: Line, { deltaX, deltaY }: DragData) => {
        if (!tableLayerOnDragStartRef.current) return;

        const delta = line.lineId.direction === 'vertical' ? deltaX : deltaY;
        const target = line.lineId.direction === 'vertical' ? 'width' : 'height';

        const getNewTableLayer = (oldTableLayer: ITableLayer): ITableLayer => {
            if (!delta) return oldTableLayer;

            const newTableLayer = cloneDeep(oldTableLayer);
            const {
                baseCells: { horizontal: horizontalSizes, vertical: verticalSizes },
            } = newTableLayer.data;

            const resolveDraggedLayer = (changedAxes: 'horizontal' | 'vertical') => {
                applyTableLayout(newTableLayer, { mode: 'interaction', changedAxes });
                // frame.addOrUpdateLayer 会将图层宽高向上取整；先使用相同终态尺寸再补偿旋转视觉原点。
                newTableLayer.width = Math.ceil(newTableLayer.width);
                newTableLayer.height = Math.ceil(newTableLayer.height);
                if (dragStartVisualOriginRef.current) {
                    alignTableToVisualOrigin(newTableLayer, dragStartVisualOriginRef.current);
                }
                return newTableLayer;
            };

            if (target === 'height') {
                const rowIndex = line.lineId.index - 1;
                const minDeltaOfHeight = rowMinSizeRef.current - verticalSizes[rowIndex];
                const deltaOfHeight = Math.max(delta, minDeltaOfHeight);
                if (delta < minDeltaOfHeight) {
                    Message.info($t((v) => v.tableLayer.behavior.limitHeight));
                }

                verticalSizes[rowIndex] += deltaOfHeight;
                return resolveDraggedLayer('vertical');
            }

            const colIndex = line.lineId.index - 1;
            const colMinSize = colMinSizesRef.current[colIndex] ?? MIN_BASE_CELL_WIDTH;

            if (horizontalSizes[colIndex] + delta <= colMinSize) {
                Message.info($t((v) => v.tableLayer.behavior.limitWidth));
            }

            const minDeltaOfWidth = colMinSize - horizontalSizes[colIndex];
            const deltaOfWidth = Math.max(delta, minDeltaOfWidth);
            horizontalSizes[colIndex] += deltaOfWidth;
            return resolveDraggedLayer('horizontal');
        };

        const oldTableLayer = tableLayerOnDragStartRef.current;
        if (!oldTableLayer) return;
        const newLayer = getNewTableLayer(oldTableLayer);
        resultTableLayerRef.current = newLayer;
    };

    return {
        handleDragLineStart,
        handleDragLine,
        handleDragLineEnd,
        resultTableLayerRef,
    };
};

const SideBarHoverArea: React.FC<{
    tableLayerData: ITableLayerProps;
    tableFakeBorder: {
        top: number;
        left: number;
        right: number;
        bottom: number;
    };
    direction: 'horizontal' | 'vertical';
    rowOrColIndex: number;
    onSelectedRowOrCol?: (rowOrColIndex: number) => void;
}> = ({ tableLayerData, tableFakeBorder, direction, rowOrColIndex, onSelectedRowOrCol }) => {
    const beforeFakeBorder = direction === 'horizontal' ? tableFakeBorder.left : tableFakeBorder.top;
    const afterFakeBorder = direction === 'horizontal' ? tableFakeBorder.right : tableFakeBorder.bottom;
    const baseSizes =
        direction === 'horizontal' ? tableLayerData.baseCells.horizontal : tableLayerData.baseCells.vertical;
    const isLastIndex = rowOrColIndex === baseSizes.length;

    const getHoverAreaStyle = (
        from: 'pre' | 'current',
    ): Partial<Record<'top' | 'left' | 'width' | 'height', number>> => {
        let size = (from === 'current' ? baseSizes[rowOrColIndex] : baseSizes[rowOrColIndex - 1]) / 2;
        let leftOrTop =
            sum(baseSizes.slice(0, rowOrColIndex)) - (from === 'pre' ? baseSizes[rowOrColIndex - 1] / 2 : 0);
        if (rowOrColIndex === 0) {
            size += beforeFakeBorder;
            leftOrTop += beforeFakeBorder;
        } else if (isLastIndex) {
            size += afterFakeBorder;
        }
        if (direction === 'vertical') {
            return {
                height: size,
                top: leftOrTop,
            };
        }
        return {
            width: size,
            left: leftOrTop,
        };
    };

    const getHoverAreaNode = (from: 'pre' | 'current') => (
        <div
            className={hoverAreaClassName}
            onMouseDown={(e) => {
                e.stopPropagation();
                onSelectedRowOrCol?.(from === 'pre' ? rowOrColIndex - 1 : rowOrColIndex);
            }}
            style={getHoverAreaStyle(from)}
        ></div>
    );

    return (
        <div>
            {rowOrColIndex !== 0 && getHoverAreaNode('pre')}
            {!isLastIndex && getHoverAreaNode('current')}
        </div>
    );
};

const useRowOrColSelectedRect = ({
    tableInfo,
    tableLayerData,
    tableFakeBorder,
}: {
    tableInfo: TableInfo;
    tableLayerData: ITableLayerProps;
    tableFakeBorder: {
        top: number;
        left: number;
        right: number;
        bottom: number;
    };
}) => {
    const framework = useFramework();
    const { setState } = useTableEditState();

    const rowOrColSelectedRectInfo = useTableEditState()((s) => s.rowOrColSelectedRectInfo);
    const { horizontal: horizontalSizes, vertical: verticalSizes } = tableLayerData.baseCells;

    type Rect = Record<'top' | 'left' | 'width' | 'height', number>;
    const selectedRect: Rect | null = (() => {
        if (!rowOrColSelectedRectInfo) return null;

        const { direction, index } = rowOrColSelectedRectInfo;
        const sizes = direction === 'horizontal' ? verticalSizes : horizontalSizes;
        const leftOrTop =
            index === 0
                ? 0
                : sum(sizes.slice(0, index)) +
                  (direction === 'horizontal' ? tableFakeBorder.top : tableFakeBorder.left);
        const beforeFakeBorder = direction === 'horizontal' ? tableFakeBorder.top : tableFakeBorder.left;
        const afterFakeBorder = direction === 'horizontal' ? tableFakeBorder.bottom : tableFakeBorder.right;
        const isLastIndex = index + 1 === sizes.length;
        const size = sizes[index] + (index === 0 ? beforeFakeBorder : isLastIndex ? afterFakeBorder : 0);

        if (direction === 'horizontal') {
            return {
                top: leftOrTop,
                left: 0,
                width: tableInfo.width,
                height: size,
            };
        }
        return {
            top: 0,
            left: leftOrTop,
            width: size,
            height: tableInfo.height,
        };
    })();

    const onSelectedRowOrCol = (info: RowOrColSelectedRectInfo) => {
        const tableLayer = framework.service.flatLayer.getLayer(tableInfo.id) as ITableLayer;
        if (!tableLayer) return;

        setState({
            activeCell: null,
            cellsIsSelecting: false,
            cellsSelectedInfo: null,
            rowOrColSelectedRectInfo: info,
        });
    };

    const selectedRectNode = !!selectedRect && <div className="rowOrColSelectedRect" style={selectedRect}></div>;

    return {
        selectedRectNode,
        onSelectedRowOrCol,
    };
};
